import { useCallback, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { useFireflyMotionSpeedContext } from "@/contexts/FireflyMotionSpeedContext";
import {
  FIREFLY_MOTION_SPEED_MAX,
  FIREFLY_MOTION_SPEED_MIN,
  FIREFLY_MOTION_SPEED_STEP,
  stepFireflyMotionSpeedMultiplier,
} from "@/lib/firefly/fireflyMotionSpeed";

export function PlatformPanelFireflyMotionSection() {
  const {
    multiplier,
    savedMultiplier,
    isDirty,
    loading,
    source,
    canEdit,
    setPreviewMultiplier,
    resetPreview,
    saveMultiplier,
  } = useFireflyMotionSpeedContext();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const onSave = useCallback(async () => {
    setSaving(true);
    setMsg(null);
    const res = await saveMultiplier(multiplier);
    setSaving(false);
    if (res.ok) {
      setMsg("Saved — applies app-wide for all users.");
    } else {
      setMsg(res.error);
    }
  }, [multiplier, saveMultiplier]);

  const stepBy = useCallback(
    (deltaSteps: number) => {
      const next = stepFireflyMotionSpeedMultiplier(multiplier, deltaSteps);
      if (next === multiplier) return;
      setMsg(null);
      setPreviewMultiplier(next);
    },
    [multiplier, setPreviewMultiplier],
  );

  const onSliderChange = useCallback(
    (value: number) => {
      if (value === multiplier) return;
      setMsg(null);
      setPreviewMultiplier(value);
    },
    [multiplier, setPreviewMultiplier],
  );

  const onReset = useCallback(() => {
    resetPreview();
    setMsg(null);
  }, [resetPreview]);

  if (!canEdit) return null;

  const atMin = multiplier <= FIREFLY_MOTION_SPEED_MIN + 1e-9;
  const atMax = multiplier >= FIREFLY_MOTION_SPEED_MAX - 1e-9;

  return (
    <SectionCard
      title="Firefly motion speed"
      description="Preview adjusts Firefly for you immediately. Save to apply app-wide. platform_owner only."
    >
      <div className="space-y-3">
        <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-zinc-200">
          <span className="flex items-center justify-between gap-2">
            <span>Speed multiplier</span>
            <span className="font-mono tabular-nums text-gray-500 dark:text-zinc-400">
              {multiplier.toFixed(1)}×
              {isDirty ? (
                <span className="ml-2 text-amber-600 dark:text-amber-400">
                  (preview)
                </span>
              ) : null}
            </span>
          </span>
          <div className="flex max-w-md items-center gap-2">
            <button
              type="button"
              aria-label="Decrease Firefly motion speed"
              disabled={loading || saving || atMin}
              onClick={() => stepBy(-1)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              −
            </button>
            <input
              type="range"
              min={FIREFLY_MOTION_SPEED_MIN}
              max={FIREFLY_MOTION_SPEED_MAX}
              step={FIREFLY_MOTION_SPEED_STEP}
              value={multiplier}
              disabled={loading || saving}
              onChange={(e) => onSliderChange(Number(e.target.value))}
              className="w-full"
              aria-label="Firefly motion speed multiplier"
            />
            <button
              type="button"
              aria-label="Increase Firefly motion speed"
              disabled={loading || saving || atMax}
              onClick={() => stepBy(1)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              +
            </button>
          </div>
          <span className="text-[11px] text-gray-500 dark:text-zinc-400">
            {FIREFLY_MOTION_SPEED_MIN}×–{FIREFLY_MOTION_SPEED_MAX}× · step{" "}
            {FIREFLY_MOTION_SPEED_STEP}× · saved: {savedMultiplier.toFixed(1)}×
            · source: {source}
            {saving ? " · saving…" : ""}
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={loading || saving || !isDirty}
            onClick={() => void onSave()}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            {saving ? "Saving…" : "Save app-wide"}
          </button>
          {isDirty ? (
            <button
              type="button"
              disabled={saving}
              onClick={onReset}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Reset
            </button>
          ) : null}
        </div>
        {msg ? (
          <p className="text-xs text-gray-600 dark:text-zinc-300">{msg}</p>
        ) : isDirty ? (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Preview only — other users still see {savedMultiplier.toFixed(1)}×
            until you save.
          </p>
        ) : null}
      </div>
    </SectionCard>
  );
}
